import { Icon, IconType } from "../../icons/Icon";
import styles from "./AppConfirmDialog.module.scss";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { DefaultButton } from "../button/defaultButton/DefaultButton";
import React from "react";
import { dialogTransitionOptions } from "../AppDialog/dialogTransition";

export type AppConfirmType = "warning" | "success";

interface IAppConfirmDialogProps {}

export const AppConfirmDialog = ({}: IAppConfirmDialogProps) => {
  // Same open and close animation as every AppDialog.
  return <ConfirmDialog transitionOptions={dialogTransitionOptions} />;
};

export interface IConfirmDialogChoice {
  label: string;
  design?: "default" | "secondary" | "outline";
  /** Runs before the dialog closes; leave it out for a button that only closes it. */
  onClick?: () => void;
}

export const showCustomConfirmDialog = (
  message: string | JSX.Element,
  accept?: () => void,
  reject?: () => void,
  type: AppConfirmType = "warning",
  mainBtnLabel = "Да",
  rejectBtnLabel: string | null = "Не"
) => {
  const choices: IConfirmDialogChoice[] = [
    { label: mainBtnLabel, design: "default", onClick: accept },
  ];

  if (rejectBtnLabel) {
    choices.push({ label: rejectBtnLabel, design: "outline", onClick: reject });
  }

  return showChoiceConfirmDialog(message, choices, type);
};

/**
 * The same dialog with any number of buttons, stacked in the given order. Use it instead of
 * opening a second dialog from a button of the first: the first one closes right after its
 * button's callback, and that closes the newly opened one too.
 */
export const showChoiceConfirmDialog = (
  message: string | JSX.Element,
  choices: IConfirmDialogChoice[],
  type: AppConfirmType = "warning"
) => {
  const iconType =
    type === "warning"
      ? IconType.Warning
      : type === "success"
      ? IconType.CheckmarkSentIcon
      : IconType.Warning;

  const color =
    type === "warning" ? "#E0BF00" : type === "success" ? "#0EAD69" : "#E0BF00";

  return confirmDialog({
    className: styles.root,
    message: (
      <div className={styles.content}>
        <Icon height="60px" width="60px" color={color} type={iconType} />
        <div className={styles.content_message}>
          {React.isValidElement(message) ? (
            message
          ) : (
            <span dangerouslySetInnerHTML={{ __html: message as string }} />
          )}
        </div>
      </div>
    ),
    dismissableMask: true,
    showHeader: false,
    footer: (options) => (
      <div className={styles.footer}>
        {choices.map((choice, index) => (
          <DefaultButton
            key={index}
            className={styles.footer_buttons_button}
            onClick={() => {
              choice.onClick?.();
              // The first button accepts, the others reject; both close the dialog.
              if (index === 0) {
                options.accept();
              } else {
                options.reject();
              }
            }}
            design={choice.design ?? "default"}
            label={choice.label}
          />
        ))}
      </div>
    ),
  });
};
